/**
 * The timezone control — V10 §4.
 *
 * One component, used by Profile, Workspace settings and the invite flow, so
 * the three cannot drift and none of them can be a text box again. That matters
 * beyond tidiness: a free-text timezone is what put `"Asia/Calcutta "` into a
 * workspace document and ended the nightly attendance sweep (§1). The only way
 * to enter a value here is to choose one that exists.
 *
 * `modal` on the popover for the same reason as §2 — this control is used
 * inside pages and dialogs alike, and a list of four hundred zones that cannot
 * be scrolled is not a list.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Globe, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  cityOf,
  groupTimezones,
  listTimezones,
  searchTimezones,
  timeInZone,
} from '@/lib/timezones';

/** The preview clocks only need to be right to the minute. */
const CLOCK_TICK_MS = 30_000;

export interface TimezonePickerProps {
  value: string;
  onChange: (zone: string) => void;
  /** Bound to the control's `<Label htmlFor>`. */
  id?: string;
  disabled?: boolean;
  /**
   * Offered first, above the full list — the workspace's timezone, which is
   * what §4 asks the control to default to. Most people share it.
   */
  suggested?: string;
  'data-testid'?: string;
}

export function TimezonePicker({
  value,
  onChange,
  id,
  disabled,
  suggested,
  'data-testid': testId = 'timezone-picker',
}: TimezonePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [now, setNow] = useState(() => new Date());
  const listRef = useRef<HTMLDivElement>(null);

  const zones = useMemo(() => listTimezones(), []);
  const groups = useMemo(() => groupTimezones(searchTimezones(zones, query)), [zones, query]);

  // The clocks beside each option have to be clocks, not a screenshot of one.
  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, [open]);

  // A reopened picker starts from the whole list, not from last time's search.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const suggestion = suggested && suggested !== value ? suggested : null;

  function choose(zone: string): void {
    onChange(zone);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          data-testid={testId}
          data-value={value}
          aria-label="Timezone"
          className="flex h-9 w-full min-w-0 items-center gap-2 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2.5 text-left text-[13px] transition-colors duration-[120ms] hover:bg-[var(--surface-2)] disabled:opacity-60"
        >
          <Globe className="size-3.5 shrink-0 opacity-60" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{value || 'Choose a timezone'}</span>
          {value && (
            <span className="vt-mono shrink-0 text-[12px] vt-muted">{timeInZone(value, now)}</span>
          )}
          <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="border-b border-[var(--border)] p-1.5">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--text-muted)]"
              aria-hidden
            />
            <Input
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                listRef.current?.scrollTo({ top: 0 });
              }}
              placeholder="City or region — “Kolkata”, “Dubai”…"
              aria-label="Search timezones"
              data-testid="timezone-search"
              className="h-7 pl-7"
            />
          </div>
        </div>

        <div
          ref={listRef}
          className="max-h-[min(20rem,var(--radix-popover-content-available-height,20rem))] overflow-y-auto overscroll-contain p-1"
          data-testid="timezone-list"
        >
          {suggestion && !query && (
            <div className="mb-1 border-b border-[var(--border)] pb-1">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide vt-muted">
                Workspace timezone
              </div>
              <ZoneOption zone={suggestion} now={now} selected={false} onSelect={choose} />
            </div>
          )}

          {groups.length === 0 ? (
            <p className="px-2 py-3 text-center text-[12px] vt-muted" data-testid="timezone-empty">
              No timezone matches “{query}”.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.region}>
                <div className="px-2 py-1 text-[10px] uppercase tracking-wide vt-muted">
                  {group.region}
                </div>
                {group.zones.map((zone) => (
                  <ZoneOption
                    key={zone}
                    zone={zone}
                    now={now}
                    selected={zone === value}
                    onSelect={choose}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ZoneOption({
  zone,
  now,
  selected,
  onSelect,
}: {
  zone: string;
  now: Date;
  selected: boolean;
  onSelect: (zone: string) => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      data-testid="timezone-option"
      data-zone={zone}
      onClick={() => onSelect(zone)}
      className={cn(
        'flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]',
        selected && 'bg-[var(--surface-2)]',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{cityOf(zone)}</span>
        <span className="block truncate text-[11px] vt-muted">{zone}</span>
      </span>
      <span className="vt-mono shrink-0 text-[12px] vt-muted">{timeInZone(zone, now)}</span>
      {selected && <Check className="size-3.5 shrink-0 text-[var(--accent)]" />}
    </button>
  );
}
